import {
  IAdditionalInfoView,
  IContactInfo,
  IContractInfo,
  IDamageInfoOnInspectionRecord,
  IEstimationItemViewModel,
  IExtendedInspectionInfo,
  IInspectionClientInfoView,
  IInspectionEstimateRequestState,
  IInspectionInfoView,
  InspectionEstimateRequestStatus,
  IInspectionLinks,
  ILocationInfoView,
  InspectionFlowType,
  IPaginatedInspection,
  IPaginationSearchInspection,
  IPanelInfoOnInspectionRecord,
  ITireFields,
  IUploadedFileDto,
  IVehicleInfoView,
  StatusInspection,
  StatusStep,
} from '@shared/services';

export enum InspectionInclude {
  InsInPho = 'photo',
  InsInVeh = 'vehicleInfo',
  InsInLoc = 'locationInfo',
  InsInInf = 'inspectionInfo',
  InsInCon = 'contactInfo',
  InsInEst = 'estimationItem',
  InsInAdd = 'additionalInfo',
  InsInNot = 'note',
  InsInReq = 'requestCorrection',
  InsInExt = 'extended_info',
  InsInLnk = 'links',
  InsInContract = 'contractInfo',
  InsInClient = 'clientInfo',
  InsInReqEst = 'estimateRequest',
}

/**
 * Convenience list of all supported `include` values.
 * Useful for validation (e.g. `IsIn(INSPECTION_INTEGRATION_INCLUDES)`).
 */
export const INSPECTION_INTEGRATION_INCLUDES = Object.values(InspectionInclude);

export enum InspectionExpand {
  CompanyName = 'locationInfo.companyName',
  LocationName = 'locationInfo.locationName',
  Location = 'locationInfo.location',
  LinksPerform = 'links.perform',
  LinksReport = 'links.report',
  LinksReportPdf = 'links.report_pdf',
  LinksAssetsZip = 'links.assets_zip',
  ContractInfoId = 'contractInfo.id',
  ContractInfoNumber = 'contractInfo.number',
}

/**
 * Convenience list of all supported `expand` values.
 * Useful for validation (e.g. `IsIn(REPORT_INTEGRATION_EXPANDS)`).
 */
export const INSPECTION_INTEGRATION_EXPANDS = Object.values(InspectionExpand);

export interface IInspectionIntegrationSearchQuery extends IPaginationSearchInspection {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  // Kept on the external integration API; the internal list endpoint uses estimateRequestStatuses instead.
  estimateRequestStatus?: InspectionEstimateRequestStatus;
  include?: InspectionInclude[];
  expand?: InspectionExpand[];
}

/**
 * Interface for inspection integration flow.
 * @interface IInspectionIntegrationFlow
 * @property {number | null} id - Unique identifier of the flow.
 * @property {string | null} title - Title of the flow.
 * @property {InspectionFlowType | string | null} [flowType] - Type of the inspection flow.
 * @property {Record<string, unknown> | null} [additionalSettings] - Additional settings for the flow.
 */
export interface IInspectionIntegrationFlow {
  id: number | null;
  title: string | null;
  flowType?: InspectionFlowType | string | null;
  additionalSettings?: Record<string, unknown> | null;
}

/**
 * Interface for inspection integration step.
 * @interface IInspectionIntegrationStep
 * @property {number | null} id - Unique identifier of the step.
 * @property {number} order - Order of the step.
 * @property {StatusStep} status - Status of the step.
 * @property {string | null} type - Type of the step.
 * @property {string | null} description - Description of the step.
 * @property {string | null} [note] - Note for the step.
 * @property {IUploadedFileDto | null} [photo] - Uploaded photo associated with the step.
 * @property {ITireFields | null} [fields] - Tire fields information.
 * @property {Record<string, unknown> | null} [additionalInformation] - Additional information for the step.
 * @property {object | null} [mask] - Mask configuration for the step.
 * @property {number | null} mask.id - Unique identifier of the mask.
 * @property {string | null} mask.type - Type of the mask.
 * @property {string | null} mask.title - Title of the mask.
 * @property {string | null} mask.subTitle - Subtitle of the mask.
 * @property {string | null} [mask.iconUrl] - Icon URL of the mask.
 * @property {string | null} [mask.maskUrl] - Mask URL.
 * @property {string | null} [mask.requiredAction] - Required action for the mask.
 * @property {boolean | null} [mask.horizontal] - Whether the mask is horizontal.
 * @property {boolean | null} [mask.vertical] - Whether the mask is vertical.
 */
export interface IInspectionIntegrationStep {
  id: number | null;
  order: number;
  status: StatusStep;
  type: string | null;
  description: string | null;
  note?: string | null;
  photo?: IUploadedFileDto | null;
  fields?: ITireFields | null;
  additionalInformation?: Record<string, unknown> | null;
  mask?: {
    id: number | null;
    type: string | null;
    title: string | null;
    subTitle: string | null;
    iconUrl?: string | null;
    maskUrl?: string | null;
    requiredAction?: string | null;
    horizontal?: boolean | null;
    vertical?: boolean | null;
  } | null;
}

/**
 * Interface for inspection integration damage record.
 * @interface IInspectionIntegrationDamage
 * @property {number | null} id - Unique identifier of the damage.
 * @property {number} order - Order of the damage record.
 * @property {string | null} status - Status of the damage.
 * @property {string | null} [typePanel] - Type of panel affected.
 * @property {string | null} [damageCategory] - Category of the damage.
 * @property {string | null} [description] - Description of the damage.
 * @property {IUploadedFileDto | null} [file] - Uploaded file associated with the damage.
 * @property {IDamageInfoOnInspectionRecord | null} [damageInfo] - Detailed damage information.
 * @property {IPanelInfoOnInspectionRecord | null} [panelInfo] - Panel information.
 */
export interface IInspectionIntegrationDamage {
  id: number | null;
  order: number;
  status: string | null;
  typePanel?: string | null;
  damageCategory?: string | null;
  description?: string | null;
  file?: IUploadedFileDto | null;
  damageInfo?: IDamageInfoOnInspectionRecord | null;
  panelInfo?: IPanelInfoOnInspectionRecord | null;
}

/**
 * Interface for inspection integration event.
 * @interface IInspectionIntegrationEvent
 * @property {number | null} id - Unique identifier of the event.
 * @property {string | null} [eventId] - External event identifier.
 * @property {number | null} [userId] - User identifier associated with the event.
 * @property {string | null} [senderService] - Service that sent the event.
 * @property {string | null} [eventName] - Name of the event.
 * @property {string | null} [statusEvent] - Status of the event.
 * @property {string | null} [payload] - Event payload data.
 * @property {string | null} [createdAt] - Creation timestamp.
 * @property {string | null} [updatedAt] - Last update timestamp.
 */
export interface IInspectionIntegrationEvent {
  id: number | null;
  eventId?: string | null;
  userId?: number | null;
  senderService?: string | null;
  eventName?: string | null;
  statusEvent?: string | null;
  payload?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Interface for inspection integration response.
 * @interface IInspectionIntegrationResponse
 * @property {string} id - Unique identifier of the inspection.
 * @property {IUploadedFileDto | null} [photo] - Uploaded photo of the inspection.
 * @property {StatusInspection | null} [status] - Status of the inspection.
 * @property {IVehicleInfoView | null} [vehicleInfo] - Vehicle information.
 * @property {ILocationInfoView | null} [locationInfo] - Location information.
 * @property {IInspectionInfoView | null} [inspectionInfo] - Inspection details.
 * @property {IContactInfo | null} [contactInfo] - Contact information.
 * @property {Partial<IContractInfo> | null} [contractInfo] - Contract information.
 * @property {IInspectionClientInfoView | null} [clientInfo] - Client information.
 * @property {IEstimationItemViewModel | null} [estimationItem] - Estimation item details.
 * @property {IAdditionalInfoView | null} [additionalInfo] - Additional information.
 * @property {string | null} [note] - Note for the inspection.
 * @property {string | null} [requestCorrection] - Request correction details.
 * @property {IExtendedInspectionInfo | null} [extended_info] - Extended inspection information.
 * @property {Partial<IInspectionLinks> | null} [links] - Inspection-related links.
 * @property {IInspectionEstimateRequestState} [estimateRequest] - State of the estimation request.
 */
export interface IInspectionIntegrationResponse {
  id: string;
  photo?: IUploadedFileDto | null;
  status?: StatusInspection | null;
  vehicleInfo?: IVehicleInfoView | null;
  locationInfo?: ILocationInfoView | null;
  inspectionInfo?: IInspectionInfoView | null;
  contactInfo?: IContactInfo | null;
  contractInfo?: Partial<IContractInfo> | null;
  clientInfo?: IInspectionClientInfoView | null;
  estimationItem?: IEstimationItemViewModel | null;
  additionalInfo?: IAdditionalInfoView | null;
  note?: string | null;
  requestCorrection?: string | null;
  extended_info?: IExtendedInspectionInfo | null;
  links?: Partial<IInspectionLinks> | null;
  estimateRequest?: IInspectionEstimateRequestState;
}

/**
 * Paginated response interface for inspection integration.
 * @interface IInspectionIntegrationPaginatedResponse
 * @extends {IPaginatedInspection<IInspectionIntegrationResponse[]>}
 */
export interface IInspectionIntegrationPaginatedResponse extends IPaginatedInspection<
  IInspectionIntegrationResponse[]
> {}
